import { useRef } from 'react';

import { useT } from '@/core/I18nProvider';

import { BILLING_PERIODS, type BillingPeriod } from './billing-period';

import type { KeyboardEvent, ReactElement } from 'react';

export interface BillingToggleProps {
  readonly value: BillingPeriod;
  readonly onChange: (next: BillingPeriod) => void;
}

/**
 * Monthly ↔ Annual segmented control shared by the home `PricingSection` and
 * the `/pricing` page. Selection is controlled by the parent (each surface owns
 * its own period state).
 *
 * A11y: an ARIA radiogroup with roving tabindex — only the selected option is
 * tab-reachable; Arrow/Home/End move AND select (selection-follows-focus, the
 * canonical radio pattern), so it is fully keyboard-operable. Arrow keys use
 * logical next/prev (not physical left/right), so it stays correct under RTL.
 */
export function BillingToggle({ value, onChange }: BillingToggleProps): ReactElement {
  const { t } = useT();
  const refs = useRef<Partial<Record<BillingPeriod, HTMLButtonElement | null>>>({});

  const labels: Record<BillingPeriod, string> = {
    monthly: t('pricing.toggle.monthly'),
    annual: t('pricing.toggle.annual'),
  };

  function select(next: BillingPeriod): void {
    if (next !== value) onChange(next);
    refs.current[next]?.focus();
  }

  function handleKeyDown(event: KeyboardEvent<HTMLButtonElement>): void {
    const index = BILLING_PERIODS.indexOf(value);
    let next: BillingPeriod | undefined;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        next = BILLING_PERIODS[(index + 1) % BILLING_PERIODS.length];
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        next = BILLING_PERIODS[(index - 1 + BILLING_PERIODS.length) % BILLING_PERIODS.length];
        break;
      case 'Home':
        next = BILLING_PERIODS[0];
        break;
      case 'End':
        next = BILLING_PERIODS[BILLING_PERIODS.length - 1];
        break;
      default:
        return;
    }
    event.preventDefault();
    if (next !== undefined) select(next);
  }

  return (
    <div
      role="radiogroup"
      aria-label={t('pricing.toggle.label')}
      className="inline-flex items-center gap-1 rounded-full border border-[color:var(--bd-line)] bg-[color:var(--bd-sunk)] p-1"
    >
      {BILLING_PERIODS.map((period) => {
        const selected = period === value;
        return (
          <button
            key={period}
            ref={(node) => {
              refs.current[period] = node;
            }}
            type="button"
            role="radio"
            aria-checked={selected}
            tabIndex={selected ? 0 : -1}
            data-period-option={period}
            onKeyDown={handleKeyDown}
            onClick={() => {
              select(period);
            }}
            // An OUTLINE rather than a ring, for the same measured reason as
            // the how-it-works steps: the SELECTED option carries `shadow-sm`,
            // and beside it `focus-visible:ring-2` does not paint — the ring
            // layers stay at their `rgba(0,0,0,0) 0 0 0 0` default while the
            // shadow occupies `box-shadow`. Focused and resting were pixel-
            // identical apart from a 2px TRANSPARENT outline, which is what
            // Tailwind's `outline-none` actually emits. `outline` is a separate
            // property and cannot be clobbered by a shadow.
            className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-medium transition focus-visible:[outline:2px_solid_var(--bd-blue)] focus-visible:[outline-offset:2px] [@media(pointer:coarse)]:min-h-11 ${
              selected
                ? 'bg-[color:var(--bd-page)] text-[color:var(--bd-ink)] shadow-sm'
                : 'text-[color:var(--bd-muted)] hover:text-[color:var(--bd-ink)]'
            }`}
          >
            {labels[period]}
            {period === 'annual' ? (
              <span className="rounded-full bg-primary/10 px-1.5 py-0.5 text-xs font-semibold text-primary-strong">
                {t('pricing.toggle.saveNote')}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
